<script setup>
import topImage from "@/assets/images/pages/fineDining1.webp"
import logoLargeLight from "@/assets/logo-large-light.svg"
import {useRestaurantsStore, useUsersStore} from "@/stores"
import appStoreImage from "@/assets/app-store2.svg"
import {ref} from "vue"
import moment from "moment/moment"
import HappeningViewerAndEditor from "@core/components/HappeningViewerAndEditor.vue"
import Testimonials from "@core/components/Testimonials.vue"
import {storeToRefs} from "pinia"

const props = defineProps({
  showSearch: {
    type: Boolean,
    default: true
  }
})
const usersStore = useUsersStore()
let myUser = null
let isRunningInIOSAppStore = null
let loginDialogMode = null
let loginDialogVerificationType = null;
({ myUser, isRunningInIOSAppStore, loginDialogMode, loginDialogVerificationType} = storeToRefs(usersStore))

const router = useRouter()

const tab = ref('Reservations')

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let markets = null
let viewingDetailsRestaurant = null;
({ restaurants, markets, viewingDetailsRestaurant  } = storeToRefs(restaurantsStore))
restaurantsStore.getRestaurants()
</script>

<template>
  <section class="home-masthead">
    <div class="home-masthead-bkg-holder">
      <img
        class="home-masthead-bkg"
        :src="topImage"
      >
    </div>
    <div class="content-wrapper ">
      <div class="masthead-content mt-4 mb-2 pl-0 pr-0">
        <h2 class="masthead-content-headline ml-2 mb-2 mt-10">
          RM's Members = Restaurants' Ideal Guests
        </h2>
        <div class="text-sm">
          Artists, Athletes, CEOs, Investors, Restaurateurs
        </div>
        <div
          class=" mt-5"
        >
          <MainSearch
            v-if="showSearch"
            class="search-bar mt-0"
            :open-menu-initially="false"
            :dont-show-initial-restaurants="true"
            search-prompt="find your free profile page"
          />
        </div>
        <p class="mt-2">
          <RouterLink
            class="text-primary"
            :to="{ path: '/discover' }"
          >
            1,000+ favorites, 100+ cities
          </RouterLink>
          <span
            class="text-caption"
            style="display: block; line-height: 0.6em;"
          >members' top spots globally</span>
        </p>
        <template v-if="!myUser || myUser.userLevel == 'dummy'">
          <div class="mt-6">
            <VBtn
              variant="flat"
              class=""
              size="small"
              color="primary"
              @click="loginDialogMode = 'activate';"
            >
              <span>Request to Join</span>
            </VBtn>
            <p class="text-caption mb-0">
              Manage your page
            </p>
          </div>
        </template>
        <br>
      </div>
    </div>
  </section>
  <div
    style="display: flex;"
    class="justify-center mt-5"
  >
    <div class="content-wrapper tab-content-width">
      <h3 class="text-center mb-2">
        Why Restaurants Love RM Members
      </h3>
      <p
        class="mt-0 text-center"
        style="line-height: 1.2em;"
      >
        RM members - Artists, Athletes, CEOs, Investors, Restaurateurs &amp; other Dining Aficionados - are the <b>Ideal Guests</b>. They guarantee a premium spend (secured with a credit card hold) to dine at their favorite spots for anniversaries, birthdays, business meetings, date nights, &amp; more.
      </p>
      <h3 class="text-center mb-2 mt-8">
        Why Members &amp; Their Request Referrals are Ideal
      </h3>
      <p
        class="mt-0 text-center"
        style="line-height: 1.2em;"
      >
        <b>-Guaranteed Spend</b>: Members guarantee a <b>premium spend</b>, secured with a <b>credit card hold</b> - vs. hoping for a "likely high spender" based on a credit card, hotel stay, or other status.
        <br><br><b>-Free for Restaurants</b>: <b>No commissions, no fees</b> - you keep <b>100%</b> of the spend! No matter how high it is - other platforms take a cut, but with RM, <b>the spend is all yours!</b> Only our members pay a membership fee, which they think is <b>nominal/a no-brainer</b> too.
        <br><br><b>-No Commitment</b>: Review &amp; accept <b>request referrals</b> only if they’re appealing. If it’s not a fit, the member will use RM for another favorite.
        <br><br><b>-Rich Profile Data</b>: Save time searching. RM members share all their details (bio, social links, contact info) upfront &amp; love for you to know who they are.
      </p>
      <h3 class="text-center mb-2 mt-8">
        How RM's App Works
      </h3>
      <p
        class="mt-0 text-center"
        style="line-height: 1.2em;"
      >
        1. Member submits a request for a <b>Preferred Reservation</b> (time n/a on OpenTable, Resy) or an Event at their favorite restaurant (any restaurant with a Google listing).<br>
        <br>2.	Request is <b>instantly shared</b> with the restaurant via email or text &amp; includes a <b>Guaranteed Spend</b> (secured with a credit card hold) &amp; their full <b>Profile</b>.<br>
        <br>3.	Restaurant reviews &amp; accepts request <b>if appealing</b>.
      </p>
      <h3 class="text-center mb-2 mt-8">
        Additional Benefits
      </h3>
      <p
        class="mt-0 text-center"
        style="line-height: 1.2em;"
      >
        <b>-Frequent Diners, Repeat Business</b>: RM members dine out often, bringing repeat business &amp; referrals from their high-value networks (fellow artists, team/league members, groups like YPO, &amp; more).
        <br><br><b>-Post Happenings &amp; Event Spaces</b>: Share your special events/experiences, special offerings, &amp; group/private spaces. Let members know where to book their next dining experience!
      </p>
      <h3 class="text-center mb-2 mt-8">
        Own Your Profile &amp; Account
      </h3>
      <p
        class="mt-0 text-center"
        style="line-height: 1.2em;"
      >
        <template v-if="!myUser || myUser.userLevel == 'dummy'">
          Create a <b>free account</b> &amp; manage your page:
          <div class="mt-1">
            <VBtn
              variant="flat"
              class=""
              size="small"
              color="primary"
              @click="loginDialogMode = 'activate';"
            >
              <span>Request to Join</span>
            </VBtn>
          </div>
        </template>
        <br><br>Questions? Email us at preferred@reserverm.com
        <br><br>Don't want referrals? Opt out at any time.
        <br>Members can then only favorite you, not submit requests.
      </p>
      <h3 class="text-center mb-2 mt-8">
        Testimonials
      </h3>
      <div class="content-wrapper tab-content-width mt-2">
        <Testimonials />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@media only screen and (max-width: 768px) {
    .masthead-content-headline{
      font-size: 1.25rem;
    }
  }
  @media only screen and (max-width: 640px) {
    .masthead-content-headline{
      font-size: 1.1rem;
    }
  }
  .search-bar {
    max-width: 350px;
    margin: auto;
  }
  .masthead-content-sub-headline {
    font-family: var(--heading-font);
    font-weight: 400;
    color: #fff;
    font-size: 1.5rem;
    line-height: 1em;
  }
  .masthead-content-logo{
    height: 2.5rem;
    width: 2.5rem;
  }
  .masthead-content{
    margin-top: 1.5em;
  }

  .overview-title{
    font-weight: 600;
    color: var(--ds-text-secondary);
    font-size: 1.2rem;
    line-height: 1em;
    margin-top: 0.5em;
  }

  .tab-content-width {
    max-width: 460px;
    font-size: 0.8em;
  }
</style>
